import React, { PropsWithChildren, useCallback, useEffect, useMemo, useState } from 'react';
import { DossierJsonldRead, Status } from '@europrocurement/l2d-domain';
import { castIriToId, getFileExtension } from '@europrocurement/l2d-utils';
import DocumentContext from './DocumentContext';
import {
    useDossierDocuments,
    type DocumentExtension,
    type DossierDocument,
    type DossierFichier,
} from '../hooks';
import { DisplayedDocument } from './types';

export type DocumentProviderProps = {
    idDossiers: Array<number>;
};

const DocumentProvider: React.FunctionComponent<PropsWithChildren<DocumentProviderProps>> =
    function ({ idDossiers, children }) {
        const [statuses, setStatuses] = useState<Array<Status>>([]);
        const [documents, setDocuments] = useState<Array<DossierDocument>>([]);
        const [fichiers, setFichiers] = useState<Array<DossierFichier>>([]);
        const [documentsLoaded, setDocumentsLoaded] = useState<boolean>(false);
        const [fichiersLoaded, setFichiersLoaded] = useState<boolean>(false);
        const [dossierFromParcours, setDossierFromParcours] = useState<DossierJsonldRead>();

        const {
            getDocumentStatuses,
            getDossierDocuments,
            getDossierFichiers,
            getFichierFromDocument,
            isNoType,
            isClientDocumentType,
            isFormalisteDocumentType,
            isIntenalDocumentType,
            getStatusIdentifier,
        } = useDossierDocuments();

        const getStatusLabel = useCallback(
            (statusId: number): string | undefined | null => {
                const status = statuses.find((s) => Number(s.id) === Number(statusId));

                return status ? status.libelle : null;
            },
            [statuses],
        );

        const addDocument = useCallback(
            (document: DossierDocument) => {
                setDocuments((docState) => [...docState, document]);
            },
            [setDocuments],
        );

        const removeDocument = useCallback(
            (documentId: number) => {
                if (
                    fichiers.filter((file) => file.typeDocDossier?.id === documentId).length === 0
                ) {
                    setDocuments((docsState) => docsState.filter((d) => d.id !== documentId));
                }
            },

            [fichiers, setDocuments],
        );

        const getFichiers = useCallback(() => fichiers, [fichiers]);

        const addFichier = useCallback(
            (fichier: DossierFichier) => {
                setFichiers([...fichiers, fichier]);
            },
            [fichiers, setFichiers],
        );

        const addMultipleFichiers = useCallback(
            (newFichiers: DossierFichier[]) => {
                setFichiers([...fichiers, ...newFichiers]);
            },
            [fichiers, setFichiers],
        );

        const removeFichier = useCallback(
            (fichier: DossierFichier) => {
                setFichiers((filesState) => filesState.filter((f) => f.id !== fichier.id));
            },
            [setFichiers],
        );

        const replaceFichierById = useCallback(
            (oldFichierId: number, newFichier: DossierFichier) => {
                const newState = fichiers.filter((f) => f.id !== oldFichierId);
                newState.push(newFichier);
                setFichiers(newState);
            },

            [fichiers, setFichiers],
        );

        const replaceFichier = useCallback(
            (oldFichier: DossierFichier, newFichier: DossierFichier) => {
                replaceFichierById(oldFichier.id as number, newFichier);
            },
            // eslint-disable-next-line react-hooks/exhaustive-deps
            [fichiers, setFichiers],
        );

        const isRequiredDocumentInError = (fichier: DossierFichier): boolean => {
            const statusId = fichier?.statut?.id;

            return undefined !== statusId && getStatusIdentifier(statusId) === 'error';
        };

        const toDisplayedDocument = (
            document: DossierDocument,
            fichier: DossierFichier | undefined,
            defaults: Record<string, unknown> | undefined = {},
        ): DisplayedDocument => {
            const statusId = fichier?.statut?.id
                ? fichier?.statut?.id
                : castIriToId(document.statut as string);
            const dossierId = fichier?.dossier
                ? castIriToId(fichier?.dossier as string)
                : castIriToId(document?.dossier as string);
            const statusLabel = getStatusLabel(statusId) as string;
            const statusIdentifier = fichier ? getStatusIdentifier(statusId) : 'pending';
            const uploadable = !fichier || statusIdentifier === 'error';
            const editable = fichier !== undefined && uploadable;
            const removable = fichier !== undefined && statusIdentifier !== 'success';
            const downloadable = fichier !== undefined && statusIdentifier === 'success';
            let label = document.libelle as string;
            label = label.charAt(0).toUpperCase() + label.slice(1);
            const fileName = fichier ? (fichier.fichier as string) : undefined;
            const extension = fileName ? getFileExtension(fileName) : undefined;
            const movable =
                fichier !== undefined &&
                extension !== 'msg' &&
                ['success', 'pending'].includes(statusIdentifier);

            return {
                id: document.id as number,
                dossierId,
                fichierId: fichier ? fichier.id : undefined,
                type: extension as DocumentExtension,
                label,
                fileName: fichier ? (fichier.fichier as string) : undefined,
                status: statusIdentifier,
                feedback: fichier ? statusLabel : undefined,
                uploading: false,
                uploadable,
                downloadable,
                showable: fichier !== undefined,
                editable,
                removable,
                movable,
                hasType: true,
                expediteurTypeId: document?.document?.type as number,
                showStatus: true,
                showUploadBtn: true,
                documentTypeId: document?.document?.id,
                file: fichier,
                ...defaults,
            };
        };

        const getAllDocuments = useCallback(
            (defaults: Record<string, unknown> | undefined = {}): DisplayedDocument[] =>
                documents
                    .filter(
                        (document: DossierDocument) => !isNoType(document?.document?.id as number),
                    )
                    .map((document) => {
                        const fichier = getFichierFromDocument(document, fichiers);

                        return toDisplayedDocument(document, fichier, { ...defaults });
                    }),
            // eslint-disable-next-line react-hooks/exhaustive-deps
            [documents, fichiers],
        );

        const getPrescriberDocuments = useCallback(
            (defaults: Record<string, unknown> | undefined = {}): DisplayedDocument[] =>
                documents
                    .filter((document: DossierDocument) => {
                        const file = getFichierFromDocument(document, fichiers);
                        const type = file
                            ? (file?.expediteur as number)
                            : (document?.document?.type as number);

                        return (
                            !isNoType(document?.document?.id as number) &&
                            isClientDocumentType(type)
                        );
                    })
                    .map((document) => {
                        const fichier = getFichierFromDocument(document, fichiers);

                        return toDisplayedDocument(document, fichier, { ...defaults });
                    }),
            // eslint-disable-next-line react-hooks/exhaustive-deps
            [documents, fichiers],
        );

        const getPrescriberAdditionalDocuments = useCallback(
            (defaults: Record<string, unknown> | undefined = {}): DisplayedDocument[] =>
                fichiers
                    .filter(
                        (fichier) =>
                            fichier?.typeDocDossier &&
                            isNoType(fichier.typeDocDossier?.document?.id as number) &&
                            isClientDocumentType(fichier.expediteur as unknown as number),
                    )
                    .map(
                        (fichier): DisplayedDocument =>
                            toDisplayedDocument(
                                fichier.typeDocDossier as DossierDocument,
                                fichier,
                                {
                                    ...defaults,
                                    hasType: false,
                                },
                            ),
                    ),
            // eslint-disable-next-line react-hooks/exhaustive-deps
            [fichiers],
        );

        const getFormalisteDocuments = useCallback(
            (defaults: Record<string, unknown> | undefined = {}): DisplayedDocument[] =>
                documents
                    .filter((document: DossierDocument) => {
                        const file = getFichierFromDocument(document, fichiers);
                        const type = file
                            ? (file?.expediteur as number)
                            : (document?.document?.type as number);

                        return (
                            !isNoType(document?.document?.id as number) &&
                            isFormalisteDocumentType(type as number)
                        );
                    })
                    .map((document): DisplayedDocument => {
                        const fichier = getFichierFromDocument(document, fichiers);

                        return toDisplayedDocument(document, fichier, { ...defaults });
                    }),
            // eslint-disable-next-line react-hooks/exhaustive-deps
            [documents, fichiers],
        );

        const getFormalisteAdditionalDocuments = useCallback(
            (defaults: Record<string, unknown> | undefined = {}): DisplayedDocument[] =>
                fichiers
                    .filter(
                        (fichier) =>
                            fichier?.typeDocDossier &&
                            isNoType(fichier.typeDocDossier?.document?.id as number) &&
                            isFormalisteDocumentType(fichier.expediteur as unknown as number),
                    )
                    .map(
                        (fichier): DisplayedDocument =>
                            toDisplayedDocument(
                                fichier.typeDocDossier as DossierDocument,
                                fichier,
                                {
                                    ...defaults,
                                    hasType: false,
                                },
                            ),
                    ),
            // eslint-disable-next-line react-hooks/exhaustive-deps
            [fichiers],
        );

        const getInternalDocuments = useCallback(
            (defaults: Record<string, unknown> | undefined = {}): DisplayedDocument[] =>
                documents
                    .filter((document: DossierDocument) => {
                        const file = getFichierFromDocument(document, fichiers);
                        const type = file
                            ? (file?.expediteur as number)
                            : (document?.document?.type as number);

                        return (
                            !isNoType(document?.document?.id as number) &&
                            isIntenalDocumentType(type as number)
                        );
                    })
                    .map((document): DisplayedDocument => {
                        const fichier = getFichierFromDocument(document, fichiers);

                        return toDisplayedDocument(document, fichier, { ...defaults });
                    }),
            // eslint-disable-next-line react-hooks/exhaustive-deps
            [documents, fichiers],
        );

        const getInternalAdditionalDocuments = useCallback(
            (defaults: Record<string, unknown> | undefined = {}): DisplayedDocument[] =>
                fichiers
                    .filter(
                        (fichier) =>
                            fichier?.typeDocDossier &&
                            isNoType(fichier.typeDocDossier?.document?.id as number) &&
                            isIntenalDocumentType(fichier.expediteur as unknown as number),
                    )
                    .map(
                        (fichier): DisplayedDocument =>
                            toDisplayedDocument(
                                fichier.typeDocDossier as DossierDocument,
                                fichier,
                                {
                                    ...defaults,
                                    hasType: false,
                                },
                            ),
                    ),
            // eslint-disable-next-line react-hooks/exhaustive-deps
            [fichiers],
        );

        const getRequiredMissingDocuments = useCallback(
            (): DisplayedDocument[] =>
                getPrescriberDocuments({}).filter(
                    (doc) => doc.fichierId === undefined || isRequiredDocumentInError(doc),
                ),
            // eslint-disable-next-line react-hooks/exhaustive-deps
            [documents, fichiers],
        );

        const getRequiredMissingDocumentsCount = useCallback(
            (): number =>
                getPrescriberDocuments({}).filter(
                    (doc) => doc.fichierId === undefined || isRequiredDocumentInError(doc),
                ).length,
            // eslint-disable-next-line react-hooks/exhaustive-deps
            [documents, fichiers],
        );

        const getDownloadableDocumentsCount = useCallback(
            (checkDocumentReady: boolean = false): number => {
                if (checkDocumentReady) {
                    return [
                        ...getFormalisteDocuments({}),
                        ...getFormalisteAdditionalDocuments({}),
                    ].filter((doc) => doc.fichierId !== undefined && doc.status === 'success')
                        .length;
                }
                return [...getFormalisteDocuments({}), ...getFormalisteAdditionalDocuments({})]
                    .length;
            },
            // eslint-disable-next-line react-hooks/exhaustive-deps
            [documents, fichiers],
        );

        const documentDataLoaded = useCallback(
            () => documentsLoaded === true && fichiersLoaded === true,
            [documentsLoaded, fichiersLoaded],
        );

        const refreshDocumentContext = useCallback(
            () => {
                const fichiersPromises = idDossiers.map((idDossier) =>
                    getDossierFichiers(idDossier),
                );
                Promise.all(fichiersPromises).then((responses) => {
                    setFichiers(responses.flat());
                    setFichiersLoaded(true);
                });

                const documentsPromises = idDossiers.map((idDossier) =>
                    getDossierDocuments(idDossier),
                );
                Promise.all(documentsPromises).then((responses) => {
                    setDocuments(responses.flat());
                    setDocumentsLoaded(true);
                });
            },
            // eslint-disable-next-line react-hooks/exhaustive-deps
            [idDossiers],
        );

        useEffect(() => {
            getDocumentStatuses().then(setStatuses);
            // eslint-disable-next-line react-hooks/exhaustive-deps
        }, []);

        useEffect(() => {
            setFichiersLoaded(false);
            setDocumentsLoaded(false);

            if (idDossiers.length > 0) {
                // fetch and setState of fichiers and documents
                refreshDocumentContext();
            } else if (dossierFromParcours) {
                // The dossier come from the parcours, it didn't come from openSearch
                const fichiersPromisesP = getDossierFichiers(dossierFromParcours.id as number);
                fichiersPromisesP.then((res) => {
                    setFichiers(res);
                    setFichiersLoaded(true);
                });

                const documentsPromiseP = getDossierDocuments(dossierFromParcours.id as number);
                documentsPromiseP.then((res) => {
                    setDocuments(res);
                    setDocumentsLoaded(true);
                });
            }
            // eslint-disable-next-line react-hooks/exhaustive-deps
        }, [idDossiers, dossierFromParcours]);

        useEffect(() => {
            if (documentDataLoaded()) {
                setDocumentsLoaded(false);

                if (idDossiers.length > 0) {
                    const promises = idDossiers.map((idDossier) => getDossierDocuments(idDossier));
                    Promise.all(promises).then((responses) => {
                        setDocuments(responses.flat());
                        setDocumentsLoaded(true);
                    });
                } else if (dossierFromParcours) {
                    const documentsPromiseP = getDossierDocuments(dossierFromParcours.id as number);
                    documentsPromiseP.then((res) => {
                        setDocuments(res);
                        setDocumentsLoaded(true);
                    });
                }
            }
            // eslint-disable-next-line react-hooks/exhaustive-deps
        }, [fichiers]);

        const contextValue = useMemo(
            () => ({
                statuses,
                setStatuses,
                getStatusLabel,
                documents,
                fichiers,
                getFichiers,
                setDocuments,
                addDocument,
                removeDocument,
                setFichiers,
                addFichier,
                addMultipleFichiers,
                removeFichier,
                replaceFichier,
                replaceFichierById,
                getRequiredMissingDocuments,
                getRequiredMissingDocumentsCount,
                getDownloadableDocumentsCount,
                getAllDocuments,
                getPrescriberDocuments,
                getPrescriberAdditionalDocuments,
                getFormalisteDocuments,
                getFormalisteAdditionalDocuments,
                getInternalDocuments,
                getInternalAdditionalDocuments,
                documentDataLoaded,
                setDossierFromParcours,
                refreshDocumentContext,
            }),
            [
                statuses,
                setStatuses,
                getStatusLabel,
                documents,
                fichiers,
                getFichiers,
                setDocuments,
                addDocument,
                removeDocument,
                setFichiers,
                addFichier,
                addMultipleFichiers,
                removeFichier,
                replaceFichier,
                replaceFichierById,
                getRequiredMissingDocuments,
                getRequiredMissingDocumentsCount,
                getDownloadableDocumentsCount,
                getAllDocuments,
                getPrescriberDocuments,
                getPrescriberAdditionalDocuments,
                getFormalisteDocuments,
                getFormalisteAdditionalDocuments,
                getInternalDocuments,
                getInternalAdditionalDocuments,
                documentDataLoaded,
                refreshDocumentContext,
                setDossierFromParcours,
            ],
        );

        return <DocumentContext.Provider value={contextValue}>{children}</DocumentContext.Provider>;
    };

export default DocumentProvider;
